import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Copy, CheckCircle2, Clock, RotateCcw, XCircle, FileDown } from "lucide-react";
import api, { API, formatApiError, getAuthToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, formatDateTime } from "@/lib/format";
import { PricingBreakdown } from "@/components/order/PricingBreakdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BADGE = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  unpaid: "bg-slate-100 text-slate-600 border-slate-200",
  refunded: "bg-blue-50 text-blue-700 border-blue-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
};

export function PaymentTab({ order, onUpdated }) {
  const { user } = useAuth();
  const isStaff = ["admin", "support"].includes(user?.role);
  const [info, setInfo] = useState(null);
  const [creditNotes, setCreditNotes] = useState([]);
  const [form, setForm] = useState({ status: "paid", payment_method: "bank_transfer", payment_date: "", reference: "", note: "", refund_amount: "", refund_date: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/orders/${order.id}/payment`).then(({ data }) => setInfo(data)).catch(() => {});
    api.get(`/orders/${order.id}/credit-notes`).then(({ data }) => setCreditNotes(data || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [order.id]);

  const apply = async (status) => {
    setBusy(true);
    try {
      const { refund_amount, refund_date, ...rest } = form;
      const body = { ...rest, status };
      if (status === "refunded") {
        if (refund_amount !== "") body.refund_amount = Number(refund_amount);
        if (refund_date) body.refund_date = refund_date;
      }
      const { data } = await api.patch(`/orders/${order.id}/payment`, body);
      onUpdated?.(data); await load(); toast.success(`Pagamento: ${status}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copyLink = () => {
    const url = info?.invoice_id ? `${window.location.origin}/invoices/${info.invoice_id}` : window.location.href;
    navigator.clipboard.writeText(url); toast.success("Link copiato");
  };

  const st = info?.payment_status || "unpaid";

  return (
    <div className="max-w-3xl space-y-6" data-testid="payment-tab">
      {isStaff && <PricingBreakdown order={order} />}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2"><CreditCard size={18} /> Pagamento</h3>
          <span className={`text-sm px-3 py-1 rounded-full border capitalize ${BADGE[st] || BADGE.unpaid}`} data-testid="payment-status-badge">{st === "unpaid" ? "In attesa di pagamento" : st}</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
          <Row label="Numero fattura" value={info?.invoice_number || "—"} testid="payment-invoice-number" />
          <Row label="Totale fattura" value={info?.invoice_total != null ? money(info.invoice_total, info.currency) : money(order.total, order.currency)} />
          <Row label="Importo pagato" value={info?.amount_paid != null ? money(info.amount_paid, info.currency) : "—"} testid="payment-amount-paid" />
          {info?.refunded_amount > 0 && (
            <>
              <Row label="Importo rimborsato" value={money(info.refunded_amount, info.currency)} testid="payment-refunded-amount" />
              <Row label="Data rimborso" value={info?.refunded_at ? formatDateTime(info.refunded_at) : "—"} testid="payment-refunded-date" />
              <Row label="Netto incassato" value={money(info?.net_paid != null ? info.net_paid : Math.max((info.amount_paid || 0) - (info.refunded_amount || 0), 0), info.currency)} testid="payment-net-paid" />
            </>
          )}
          <Row label="Metodo" value={info?.payment_method || "—"} />
          <Row label="Data pagamento" value={info?.payment_date ? formatDateTime(info.payment_date) : "—"} />
          <Row label="Riferimento" value={info?.payment_reference || "—"} />
          <Row label="Stripe session" value={info?.stripe_session_id || "—"} />
        </div>
        {isStaff && (
          <Button variant="outline" size="sm" className="mt-4" onClick={copyLink} data-testid="payment-copy-link"><Copy size={14} className="mr-1.5" /> Copia link pagamento</Button>
        )}
        {creditNotes.length > 0 && (
          <div className="mt-5 border-t border-slate-100 pt-4" data-testid="credit-notes-list">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Note di credito</div>
            <div className="space-y-2">
              {creditNotes.map((n) => (
                <a key={n.id} href={`${API}/invoices/credit-notes/${n.id}/pdf?auth=${getAuthToken()}`} target="_blank" rel="noopener noreferrer"
                   className="flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2 text-sm hover:bg-rose-50 transition-colors"
                   data-testid="credit-note-download">
                  <span className="flex items-center gap-2 text-rose-700 font-medium"><FileDown size={15} /> {n.number}</span>
                  <span className="text-rose-600">-{money(n.total, n.currency)}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {isStaff && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="manual-payment-panel">
          <h4 className="font-medium text-slate-800 mb-4">Conferma manuale del pagamento</h4>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Metodo</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="manual-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bonifico bancario</SelectItem>
                  <SelectItem value="cash">Contanti</SelectItem>
                  <SelectItem value="external">Esterno</SelectItem>
                  <SelectItem value="stripe">Stripe</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Data pagamento</Label>
              <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} data-testid="manual-date" /></div>
            <div className="space-y-1.5"><Label>Riferimento (opzionale)</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="manual-reference" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Nota interna</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="manual-note" /></div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button onClick={() => apply("paid")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mark-paid-btn"><CheckCircle2 size={16} className="mr-1.5" /> Segna come Pagato</Button>
            <Button onClick={() => apply("pending")} disabled={busy} variant="outline" data-testid="mark-pending-btn"><Clock size={16} className="mr-1.5" /> Pending</Button>
            <Button onClick={() => apply("failed")} disabled={busy} variant="outline" className="text-rose-600" data-testid="mark-failed-btn"><XCircle size={16} className="mr-1.5" /> Fallito</Button>
          </div>
        </div>
      )}

      {isStaff && (
        <div className="bg-white rounded-xl border border-blue-200 shadow-sm p-6" data-testid="refund-panel">
          <h4 className="font-medium text-slate-800 mb-1 flex items-center gap-2"><RotateCcw size={16} className="text-blue-700" /> Rimborso</h4>
          <p className="text-xs text-slate-500 mb-4">Indica l'importo esatto rimborsato e la data. Lascia vuoto l'importo per rimborsare l'intero pagato.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Importo rimborsato ({info?.currency || order.currency || "EUR"})</Label>
              <Input type="number" step="0.01" min="0" placeholder={info?.amount_paid != null ? String(info.amount_paid) : "0.00"}
                value={form.refund_amount} onChange={(e) => setForm({ ...form, refund_amount: e.target.value })} data-testid="refund-amount" /></div>
            <div className="space-y-1.5"><Label>Data rimborso</Label>
              <Input type="date" value={form.refund_date} onChange={(e) => setForm({ ...form, refund_date: e.target.value })} data-testid="refund-date" /></div>
          </div>
          <Button onClick={() => apply("refunded")} disabled={busy} className="mt-4 bg-blue-700 hover:bg-blue-800" data-testid="mark-refunded-btn"><RotateCcw size={16} className="mr-1.5" /> Registra rimborso</Button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-4" data-testid={testid}>
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 font-medium text-right break-all">{value}</span>
    </div>
  );
}
